/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Page Settings Statistics
 *
 * @author Anton Panasenko
 * @version 1.4.0
 * @since 1.3.1
 */

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { DropFiltersButton } from '../../../../../components/common';
import { dropTableFilterAndSort } from '../../../../../components/Table/actions/tableActions';
import {
    makeSelectorTableCount,
    makeSelectorTableIsFilterSelected,
} from '../../../../../components/Table/selectors/tableSelectors';
import { TableNameType } from '../../../../../components/Table/types/tableTypes';
import { EXPORT_SETTINGS_STATISTICS } from '../../../../../modules/app/Api';
import ExportButton from '../../../../../modules/exports/components/ExportButton';
import { makeSelectPageSettingsStatisticsTranslation } from '../../../../../modules/internationalization/intSelectors';
import {
    IPageSettingsStatisticsTranslation,
} from '../../../../../modules/internationalization/types/pages/pageSettings';
import { IRootReducer } from '../../../../../reducers/rootReducer';
import {
    PageSettingsCounterStyled,
    PageSettingsHeaderContainerStyled,
    PageSettingsHeaderStyled,
    PageTitleElementsContainerStyled,
} from '../../styled/PageSettingsStyled';
import { SettingsStatisticsContainerStyled } from '../styled/SettingsStatisticsStyled';
import SettingsStatisticsTable from './SettingsStatisticsTable/SettingsStatisticsTable';

type TSettingsStatisticsProps = IConnectedProps & IDispatchProps;

const SettingsStatistics = (props: TSettingsStatisticsProps) => (
    <SettingsStatisticsContainerStyled>
        <PageSettingsHeaderContainerStyled>
            <PageSettingsHeaderStyled>
                { props.settingsStatisticsTranslation.title.header }
                <PageSettingsCounterStyled>
                    { props.tableCount }
                </PageSettingsCounterStyled>
            </PageSettingsHeaderStyled>

            <PageTitleElementsContainerStyled>
                {
                    props.isFilterSelected &&
                    <DropFiltersButton
                        label={props.settingsStatisticsTranslation.title.dropFilters}
                        onClick={props.dropFilters}
                    />
                }

                <ExportButton
                    src={EXPORT_SETTINGS_STATISTICS}
                    tableName={TableNameType.settingsStatistics}
                />
            </PageTitleElementsContainerStyled>
        </PageSettingsHeaderContainerStyled>

        <SettingsStatisticsTable
            tableCount={props.tableCount}
        />
    </SettingsStatisticsContainerStyled>
)

interface IConnectedProps {
    isFilterSelected: boolean;
    settingsStatisticsTranslation: IPageSettingsStatisticsTranslation;
    tableCount: number;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isFilterSelected: makeSelectorTableIsFilterSelected(TableNameType.settingsStatistics),
    settingsStatisticsTranslation: makeSelectPageSettingsStatisticsTranslation(),
    tableCount: makeSelectorTableCount(TableNameType.settingsStatistics),
});

interface IDispatchProps {
    dropFilters: () => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    dropFilters: () => dispatch(dropTableFilterAndSort({ tableName: TableNameType.settingsStatistics })),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsStatistics);
